import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBar } from '@angular/material/progress-bar';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';

/** Navigations quicker than this never flash the bar. */
export const ROUTE_LOADING_DELAY_MS = 150;

/** Matches the splash's ce-boot-out animation in index.html. */
const SPLASH_FADE_MS = 200;

/**
 * RouteLoading — what the user sees while the router is busy.
 *
 * Every route is lazy and the admin tree sits behind the auth, org-access and
 * season guards, each of which waits on Firebase. Until they resolve the
 * outlet is empty, which read as a broken page on refresh.
 *
 * - First load: index.html paints a `#boot-splash` before any JS runs; this
 *   fades it out once the first route has rendered. A guard that redirects
 *   (signed out → sign-in) cancels the first navigation and starts another,
 *   so the splash stays until that one lands.
 * - Afterwards: a thin indeterminate bar pinned to the top of the viewport,
 *   shown once a navigation has taken longer than {@link ROUTE_LOADING_DELAY_MS}.
 *
 * Mounted once, in the root component.
 */
@Component({
  selector: 'ce-route-loading',
  imports: [MatProgressBar],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (showBar()) {
      <mat-progress-bar
        class="!fixed inset-x-0 top-0 z-[1000]"
        mode="indeterminate"
        aria-label="Loading page"
        data-testid="route-loading-bar"
      />
    }
  `,
})
export class RouteLoading {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  protected readonly showBar = signal(false);

  private booted = false;
  private barTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.barTimer));

    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        clearTimeout(this.barTimer);
        if (this.booted) {
          this.barTimer = setTimeout(() => this.showBar.set(true), ROUTE_LOADING_DELAY_MS);
        }
      } else if (event instanceof NavigationEnd) {
        this.settle();
        this.finishBoot();
      } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.settle();
        // A redirecting guard cancels this navigation and has already queued
        // the next one; only give up on the splash if nothing follows.
        setTimeout(() => {
          if (!this.router.currentNavigation()) this.finishBoot();
        });
      }
    });
  }

  private settle(): void {
    clearTimeout(this.barTimer);
    this.showBar.set(false);
  }

  private finishBoot(): void {
    if (this.booted) return;
    this.booted = true;
    const splash = this.document.getElementById('boot-splash');
    if (!splash) return;
    splash.classList.add('ce-boot--out');
    setTimeout(() => splash.remove(), SPLASH_FADE_MS);
  }
}
